// Authentication — Auth0 is the only login method this site has (there's no
// admin-key fallback the way cloudish-admin has one; this is public signup).
//
// The access token this gets is sent as a bearer credential to *this app's
// own* backend (see api.ts) on every /session/* call, verified fresh each
// time (server/src/auth0.js) — it is never sent to the control plane
// directly, and the control plane never learns Auth0 exists.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { setTokenProvider, setUnauthorizedHandler } from './api';
import type { SessionConfig } from './types';

interface Profile {
  /** The same `sub` this site's own OIDC provider puts in an app's id_token. */
  sub?: string;
  email: string;
  name?: string;
}

interface AuthState {
  config: SessionConfig | null;
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  /** True once a session that looked signed in turned out to be dead —
   *  lets a page say "sign in again" rather than just "sign in". */
  sessionExpired: boolean;
  /** Why the last trip through Auth0 came back without a session (consent
   *  denied, a signup rule said no, …) — for a sign-in screen to say so. */
  signInError: string | null;
  login: (returnTo?: string) => Promise<void>;
  /** `then` is where to land afterwards instead of this site's home page —
   *  only ever a URL the server already approved (see OAuthLogout.tsx). */
  logout: (then?: string) => Promise<void>;
}

// Auth0 only returns from its logout to this site's own origin (the one
// "Allowed Logout URL" registered), so a sign-out that should end up back
// in an app parks that destination here first and the next page load
// resumes it. Same-origin storage, written only by logout() below, short
// lived — never anything a link can set.
const AFTER_LOGOUT_KEY = 'cloudish:after-logout';
const AFTER_LOGOUT_TTL_MS = 2 * 60 * 1000;

function takeAfterLogout(): string | null {
  try {
    const raw = sessionStorage.getItem(AFTER_LOGOUT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(AFTER_LOGOUT_KEY);
    const { url, at } = JSON.parse(raw) as { url?: string; at?: number };
    return url && at && Date.now() - at < AFTER_LOGOUT_TTL_MS ? url : null;
  } catch {
    return null;
  }
}

// Auth0 SDK error codes meaning the session itself is gone (refresh token
// expired, rotated away, or revoked; SSO session over) — as opposed to a
// network blip, which shouldn't sign anyone out.
const SESSION_GONE = new Set(['login_required', 'missing_refresh_token', 'invalid_grant', 'consent_required', 'interaction_required']);

function isSessionGone(err: unknown) {
  return SESSION_GONE.has((err as { error?: string } | null)?.error ?? '');
}

/** Where Auth0 hands every sign-in back (the redirect_uri), the "Sign in
 *  with Cloudish" round trip included: a page of its own (App.tsx), so no
 *  other page needs a special case for it. It must be in the Auth0
 *  application's Allowed Callback URLs for every origin this site is served
 *  on (README.md). */
export const AUTH_CALLBACK_PATH = '/auth/callback';

/** Whether this URL is Auth0 handing a sign-in back: `code` and `state` when
 *  it worked, `error` and `state` when it didn't. On AUTH_CALLBACK_PATH, or
 *  on `/`, the redirect_uri before it: a sign-in started before the move
 *  still comes back there, and still finishes. */
export function isAuthReturn(pathname: string, search: string) {
  if (pathname !== AUTH_CALLBACK_PATH && pathname !== '/') return false;
  const params = new URLSearchParams(search);
  return Boolean(params.get('state') && (params.get('code') || params.get('error')));
}

const AuthContext = createContext<AuthState | null>(null);

async function sessionRequest<T>(path: string): Promise<T> {
  const response = await fetch(path);
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error((payload as { error?: string })?.error || 'request failed');
  return payload as T;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [config, setConfig] = useState<SessionConfig | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const clientRef = useRef<Auth0Client | null>(null);
  const signedInRef = useRef(false);

  // `isAuthenticated()` only means "an ID token is cached" — that cache
  // never expires on its own, so it outlives the refresh token it came
  // with. Without this, a dead session kept rendering as signed in while
  // every /session/* call went out with no token and 401'd. Clears the
  // local cache only (no redirect), so the page drops to its signed-out
  // state and its own sign-in button returns to the same URL.
  const expireSession = useCallback(async () => {
    if (!signedInRef.current) return;
    signedInRef.current = false;
    setProfile(null);
    setSessionExpired(true);
    await clientRef.current?.logout({ openUrl: false }).catch(() => {});
  }, []);

  useEffect(() => {
    setTokenProvider(async () => {
      const client = clientRef.current;
      if (!client) return null;
      try {
        return (await client.getTokenSilently()) ?? null;
      } catch (err) {
        if (isSessionGone(err)) await expireSession();
        return null;
      }
    });
    setUnauthorizedHandler(() => expireSession());
  }, [expireSession]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const afterLogout = takeAfterLogout();
      if (afterLogout) {
        window.location.replace(afterLogout);
        return;
      }
      try {
        const session = await sessionRequest<SessionConfig>('/session/config');
        if (cancelled) return;
        setConfig(session);

        if (session.enabled && session.domain && session.clientId) {
          const client = new Auth0Client({
            domain: session.domain,
            clientId: session.clientId,
            authorizationParams: {
              redirect_uri: `${window.location.origin}${AUTH_CALLBACK_PATH}`,
              audience: session.audience,
              scope: 'openid profile email',
            },
            cacheLocation: 'localstorage',
            useRefreshTokens: true,
          });
          clientRef.current = client;

          if (isAuthReturn(window.location.pathname, window.location.search)) {
            try {
              const result = await client.handleRedirectCallback();
              const target = (result.appState as { returnTo?: string } | undefined)?.returnTo || '/';
              navigate(target, { replace: true });
            } catch (err) {
              // An `error` return still carries the appState it left with
              // (AuthenticationError), so it goes back to where it started,
              // signed out, with the reason. A stale or replayed link has no
              // transaction to match and just goes home.
              const appState = (err as { appState?: { returnTo?: string } } | null)?.appState;
              setSignInError((err instanceof Error && err.message) || (err as { error?: string } | null)?.error || 'sign-in failed');
              navigate(appState?.returnTo || '/', { replace: true });
            }
          }

          if (await client.isAuthenticated()) {
            // Prove the session is still usable before rendering anyone as
            // signed in (see expireSession above) — instant when a valid
            // access token is cached, a refresh otherwise.
            const alive = await client.getTokenSilently().then(
              () => true,
              (err) => !isSessionGone(err)
            );
            const user = alive ? await client.getUser() : null;
            if (user?.email) {
              signedInRef.current = true;
              setProfile({ sub: user.sub, email: user.email, name: user.name });
            } else if (!alive) {
              setSessionExpired(true);
              await client.logout({ openUrl: false }).catch(() => {});
            }
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'failed to load configuration');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (returnTo?: string) => {
      const client = clientRef.current;
      if (!client) {
        setError('Sign-in is not configured on this deployment');
        return;
      }
      await client.loginWithRedirect({
        appState: { returnTo: returnTo || `${location.pathname}${location.search}` },
      });
    },
    [location]
  );

  const logout = useCallback(
    async (then?: string) => {
      const client = clientRef.current;
      signedInRef.current = false;
      setProfile(null);
      if (client) {
        const isAuthenticated = await client.isAuthenticated().catch(() => false);
        if (isAuthenticated) {
          if (then) sessionStorage.setItem(AFTER_LOGOUT_KEY, JSON.stringify({ url: then, at: Date.now() }));
          await client.logout({ logoutParams: { returnTo: window.location.origin } });
          return;
        }
      }
      if (then) window.location.replace(then);
      else navigate('/');
    },
    [navigate]
  );

  const value = useMemo<AuthState>(
    () => ({ config, profile, loading, error, sessionExpired, signInError, login, logout }),
    [config, profile, loading, error, sessionExpired, signInError, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
